// APP-104 §3.2 (Menos lesson: "Microphone access was declined" helped nobody): why the
// microphone didn't open, and the one thing the person can do about it. A browser never asks again
// once a site is blocked, so the help names the exact control for this browser. Ported from
// Menos's `service/web/src/micAccess.js` without its desktop-app bridge: MyRewards is web only.
// The browser's own error message is never shown; every sentence here is fixed.

export type MicrophoneHelpKind = 'blocked' | 'system' | 'dismissed' | 'missing' | 'busy' | 'other';

export interface MicrophoneHelp {
  /**
   * `blocked` (this site), `system` (the computer blocks the browser), `dismissed` (the question
   * was closed; trying again asks again), `missing`, `busy` or `other`.
   */
  kind: MicrophoneHelpKind;
  title: string;
  step: string;
}

export type BrowserName =
  'chrome' | 'edge' | 'firefox' | 'safari' | 'ios-safari' | 'ios-other' | 'android' | 'browser';

/** The parts of `navigator` this reads; tests pass their own. */
export interface Agent {
  userAgent?: string;
  userAgentData?: { brands?: readonly { brand: string }[] } | undefined;
  maxTouchPoints?: number;
}

/** The site's microphone permission, as this browser reports it. */
export type PermissionReader = () => Promise<PermissionState | 'unknown'>;

const DENIED = new Set(['NotAllowedError', 'PermissionDeniedError', 'SecurityError']);
const MISSING = new Set(['NotFoundError', 'DevicesNotFoundError', 'OverconstrainedError']);
const BUSY = new Set(['NotReadableError', 'TrackStartError', 'AbortError']);
/** The microphone didn't open in time (the recorder's `MicrophoneStartTimeout`). */
const STUCK = 'MicrophoneStartTimeout';

const nameOf = (error: unknown): string =>
  typeof error === 'object' &&
  error !== null &&
  typeof (error as { name?: unknown }).name === 'string'
    ? (error as { name: string }).name
    : '';

const messageOf = (error: unknown): string =>
  typeof error === 'object' &&
  error !== null &&
  typeof (error as { message?: unknown }).message === 'string'
    ? (error as { message: string }).message
    : '';

/** True when getUserMedia failed because of permission or the device, not the app or network. */
export function isMicrophoneError(error: unknown): boolean {
  const name = nameOf(error);
  return DENIED.has(name) || MISSING.has(name) || BUSY.has(name) || name === STUCK;
}

export function browserName(agent: Agent = navigator): BrowserName {
  const ua = agent.userAgent ?? '';
  const brands = (agent.userAgentData?.brands ?? []).map((b) => b.brand);
  if (/iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && (agent.maxTouchPoints ?? 0) > 1))
    return /CriOS|FxiOS|EdgiOS/.test(ua) ? 'ios-other' : 'ios-safari';
  if (/Android/.test(ua)) return 'android';
  if (brands.includes('Microsoft Edge') || /Edg\//.test(ua)) return 'edge';
  if (/Firefox\//.test(ua)) return 'firefox';
  if (/Chrome\//.test(ua)) return 'chrome';
  if (/Safari\//.test(ua)) return 'safari';
  return 'browser';
}

function platformName(agent: Agent): 'windows' | 'mac' | 'other' {
  const ua = agent.userAgent ?? '';
  if (/Windows/.test(ua)) return 'windows';
  if (/Macintosh|Mac OS X/.test(ua)) return 'mac';
  return 'other';
}

/** The site's microphone permission: granted, denied, prompt, or unknown where it can't be read. */
export async function microphonePermission(): Promise<PermissionState | 'unknown'> {
  try {
    return (await navigator.permissions.query({ name: 'microphone' })).state;
  } catch {
    return 'unknown';
  }
}

const APP: Partial<Record<BrowserName, string>> = {
  chrome: 'Google Chrome',
  edge: 'Microsoft Edge',
  firefox: 'Firefox',
  safari: 'Safari',
};

function blockedStep(browser: BrowserName, host: string): string {
  switch (browser) {
    case 'chrome':
      return 'Click the icon at the left end of the address bar, turn on Microphone, then try again.';
    case 'edge':
      return 'Click the icon at the left end of the address bar, set Microphone to Allow, then try again.';
    case 'firefox':
      return 'Click the crossed-out microphone in the address bar, clear the block, then try again.';
    case 'safari':
      return `In the menu bar choose Safari › Settings for ${host}, set Microphone to Allow, then try again.`;
    case 'ios-safari':
      return 'Tap the page menu at the left of the address bar, choose Website Settings, set Microphone to Allow, then try again.';
    case 'android':
      return 'Tap the icon at the left end of the address bar, choose Permissions, turn on Microphone, then try again.';
    default:
      return `Allow the microphone for ${host} in this browser’s site settings, then try again.`;
  }
}

function systemStep(browser: BrowserName, platform: 'windows' | 'mac' | 'other'): string {
  const app = APP[browser] ?? 'this browser';
  if (platform === 'windows')
    return 'Open Windows Settings › Privacy & security › Microphone, let desktop apps use the microphone, then try again.';
  if (platform === 'mac')
    return `Open System Settings › Privacy & Security › Microphone, turn on ${app}, then quit and reopen ${app}.`;
  if (browser === 'ios-other')
    return 'Open Settings › Privacy & Security › Microphone, turn on this browser, then try again.';
  return 'Allow this browser to use the microphone in your device settings, then try again.';
}

/** Explains a failed microphone request in fixed words for this browser. */
export async function explainMicrophone(
  error: unknown,
  {
    host = location.hostname || 'this site',
    agent = navigator,
    permission = microphonePermission,
  }: { host?: string; agent?: Agent; permission?: PermissionReader } = {},
): Promise<MicrophoneHelp> {
  const name = nameOf(error);
  const message = messageOf(error);
  const browser = browserName(agent);
  const platform = platformName(agent);
  if (DENIED.has(name)) {
    // Chromium names an operating-system block in its message; otherwise the permission state
    // tells a blocked site (denied) from a closed question (still prompt).
    if (/by system/i.test(message)) {
      const device = platform === 'windows' ? 'computer' : platform === 'mac' ? 'Mac' : 'device';
      return {
        kind: 'system',
        title: `Your ${device} is blocking ${APP[browser] ?? 'this browser'} from the microphone`,
        step: systemStep(browser, platform),
      };
    }
    const state = await permission();
    if (state === 'prompt' || /dismissed/i.test(message))
      return {
        kind: 'dismissed',
        title: 'MyRewards needs your microphone',
        step: 'Try again and choose Allow when your browser asks.',
      };
    if (state === 'granted')
      return {
        kind: 'system',
        title: 'Your device is blocking the microphone',
        step: systemStep(browser, platform),
      };
    return {
      kind: 'blocked',
      title: `Microphone is blocked for ${host}`,
      step: blockedStep(browser, host),
    };
  }
  if (MISSING.has(name))
    return {
      kind: 'missing',
      title: 'No microphone found',
      step:
        browser === 'firefox' && platform === 'mac'
          ? 'Connect a microphone, then try again. If one is connected, turn on Firefox in System Settings › Privacy & Security › Microphone.'
          : 'Connect a microphone, then try again.',
    };
  // Still waiting on the browser's question: the same help as a closed one (and it's watched, so
  // an Allow given now starts recording). Otherwise the device didn't answer: as if busy.
  if (name === STUCK && (await permission()) === 'prompt')
    return {
      kind: 'dismissed',
      title: 'MyRewards needs your microphone',
      step: 'Try again and choose Allow when your browser asks.',
    };
  if (BUSY.has(name) || name === STUCK)
    return {
      kind: 'busy',
      title: 'Your microphone couldn’t start',
      step: 'Another app may be using it. Close that app, then try again.',
    };
  return {
    kind: 'other',
    title: 'Voice couldn’t start',
    step: 'Try again, or type what happened.',
  };
}

/**
 * Calls `onAllowed` once, when the browser reports the site's microphone permission granted, so
 * the person doesn't have to come back and press anything. Only a blocked site or a closed
 * question is watched: for a missing or busy microphone permission isn't the problem.
 */
export function watchMicrophone(
  help: MicrophoneHelp,
  onAllowed: () => void,
  query: () => Promise<PermissionStatus> = () =>
    navigator.permissions.query({ name: 'microphone' }),
): () => void {
  // Read through an object: the stop below can come while the query is still answering.
  const watch: { stopped: boolean; status: PermissionStatus | null } = {
    stopped: false,
    status: null,
  };
  const change = (): void => {
    if (watch.stopped || watch.status?.state !== 'granted') return;
    watch.stopped = true;
    onAllowed();
  };
  if (help.kind === 'blocked' || help.kind === 'dismissed')
    void (async () => {
      try {
        const status = await query();
        if (watch.stopped) return;
        watch.status = status;
        status.addEventListener('change', change);
      } catch {
        // A browser that can't report the permission: Try again still works.
      }
    })();
  return () => {
    watch.stopped = true;
    watch.status?.removeEventListener('change', change);
  };
}
